import { useState, useMemo } from "react";
import { Box, IconButton, Paper } from "@mui/material";
import { Delete, Edit } from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import DG_CONFIG from "../../config/data-grid";
import { useQuery } from "@tanstack/react-query";
import { addIdForDataGrid, applyColumnWidths, disableHideableColumns } from "../../helpers/data-grid";
import FiltersWrapper from "../../common/components/FiltersWrapper";
import SearchBar from "../../common/components/SearchBar";
import AddButton from "../../common/components/AddButton";
import ExportButton from "../../common/components/ExportButton";
import { confirm } from "../../common/services/confirm-service";
import { toast } from "react-toastify";
import TOASTER from "../../config/toaster";
import logger from "../../helpers/logger";
import {
  COMMON_MSG,
  DELETE_CONTENT,
  DELETE_TITLE,
} from "../../common/messages";
import { appInsights } from "../../appInsights";
import { useUserName } from "../../helpers/username";
import useResponsiveConfig from "../../config/responsive-style";
import HelpIcon from "@mui/icons-material/Help";
import { getComponentPreference, setComponentPreference } from "../../common/services/cookie-service";
import { COLUMN_VISIBILITY_PREF_KEY, COLUMN_WIDTH_PREF_KEY, PAGE_PREF_KEY, SORT_PREF_KEY } from "../../enums";
import { PAGE_KEYS } from "../../config/permissions/page-key";
import RoleButton from "../../common/components/RoleButton";
import { BUTTON_TYPES } from "../../config/permissions";
import { deleteTimeFrameData, fetchTimeFrameData } from "../../api/timesetup-api-service";
import TimeFrameHelp from "./TimeFrameHelp";
import TimeFrameDialog from "./TimeFrameDialog";

const CONFIG = {
  defaultFormData: {
    timeFrame_Desc: "",
    timeFrame_Time: "",
  },
};

const TIMEFRAME = "time-frame";
const TIMEFRAME_SEARCH = "timeframeSearch";
const defaultSorting = [{ field: "timeFrame_Desc", sort: "desc" }];

const TimeFrame = () => {
  const initialSearch = sessionStorage.getItem(TIMEFRAME_SEARCH);
  const initialPageSize = getComponentPreference(TIMEFRAME, PAGE_PREF_KEY, DG_CONFIG.ROW_PER_PAGE);
  const initialSorting = getComponentPreference(TIMEFRAME, SORT_PREF_KEY, defaultSorting);
  const initialColumnVisibility = getComponentPreference(TIMEFRAME, COLUMN_VISIBILITY_PREF_KEY);
  const [columnWidths, setColumnWidths] = useState(getComponentPreference(TIMEFRAME, COLUMN_WIDTH_PREF_KEY, {}));
  const [formData, setFormData] = useState(CONFIG.defaultFormData);
  const [dialogState, setDialogState] = useState(null); // null - Close, 1 - Add, 2 - Edit
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(initialSearch || "");
  const userName = useUserName();
  const [helpOpen, setHelpOpen] = useState(false);

  const { isLoading, data, refetch } = useQuery({
    refetchOnMount: false,
    queryKey: ["timeFrameData"],
    queryFn: async () => {
      const res = await fetchTimeFrameData();
      if (res.data) {
        logger.log("Data fetch success:", res.data);
      }
      return res.data || [];
    },
  });

  const { FILTER_BUTTON_STYLE, DATAGRID_STYLE } = useResponsiveConfig();

  const handleEdit = (row) => {
    logger.log("Edit event triggered for row:", row);
    setFormData(row);
    setDialogState(2);
  };

  const handleDelete = async (row) => {
    logger.log("Delete event triggered for row:", row);
    const res = await confirm({ title: DELETE_TITLE, content: DELETE_CONTENT });

    if (res) {
      await processDelete(row?.timeFrame_Key);
    }
  };

  const processDelete = async (id) => {
    if (id) {
      // Telemetry for delete
      appInsights.trackEvent({
        name: `TimeFrame_Deleted`,
        properties: {
          user: userName,
          recordId: id,
          timestamp_utc: new Date().toISOString(),
          timestamp_local: new Date().toLocaleString(),
        },
      });
      let toastId = toast.loading(COMMON_MSG.Processing);
      try {
        const response = await deleteTimeFrameData(id, toastId);
        if (response.isSuccess) {
          logger.log("Delete success for id:", id);
          await refetch();
          toast.update(toastId, {
            render: response.message,
            type: "success",
            isLoading: false,
            autoClose: TOASTER.AUTO_CLOSE,
          });
        }
      } catch (error) {
        logger.log(error);
      }
    }
  };

  const handleAdd = () => {
    logger.log("Add event triggered");
    setFormData(CONFIG.defaultFormData);
    setDialogState(1);
  };

  const rawColumns = [
    {
      field: "timeFrame_Desc",
      headerName: "Description",
      minWidth: 120,
      flex: 0.4,
      sortable: true,
    },
    {
      field: "timeFrame_Time",
      headerName: "Time",
      minWidth: 120,
      flex: 0.5,
      ignoreSearch: true,
      sortable: true,
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.2,
      filterable: false,
      sortable: false,
      minWidth: 100,
      renderCell: (params) => (
        <>
          <RoleButton
            roleButtonType={BUTTON_TYPES.EDIT}
            roleComponent={PAGE_KEYS.TIME_SETUP_FRAME}
            roleIsIconBtn
            onClick={() => handleEdit(params.row)}
            color="primary"
            tabIndex={0}
            size="small"
            disabled={isLoading}
          >
            <Edit />
          </RoleButton>
          <RoleButton
            roleButtonType={BUTTON_TYPES.DELETE}
            roleComponent={PAGE_KEYS.TIME_SETUP_FRAME}
            roleIsIconBtn
            onClick={() => handleDelete(params.row)}
            color="error"
            tabIndex={0}
            size="small"
            disabled={isLoading}
          >
            <Delete />
          </RoleButton>
        </>
      ),
    },
  ];

  const columns = useMemo(
    () => {
      const hideableColumns = disableHideableColumns(rawColumns, ["timeFrame_Desc", "actions"]);
      return applyColumnWidths(hideableColumns, columnWidths);
    }, [handleEdit, handleDelete, columnWidths]
  );

  const dataWithId = useMemo(() => addIdForDataGrid(data || []), [data]);

  const getGridData = useMemo(() => {
    let filteredGridData = Array.isArray(dataWithId) ? dataWithId : [];

    // Apply search filter
    if (debouncedSearchTerm) {
      filteredGridData = filteredGridData.filter((row) =>
        columns
          .filter((i) => i.ignoreSearch === undefined)
          .some((col) => {
            let value = row[col.field];

            return (value ?? "")
              .toString()
              .toLowerCase()
              .includes(debouncedSearchTerm.toLowerCase());
          })
      );
    }

    return filteredGridData;
  }, [debouncedSearchTerm, dataWithId]);

  const handlePageSizeChange = (newSize) => {
    setComponentPreference(TIMEFRAME, PAGE_PREF_KEY, newSize?.pageSize);
  };

  const handleSortChange = (value) => {
    setComponentPreference(TIMEFRAME, SORT_PREF_KEY, value);
  };

  const handleColumnVisibilityChange = (value) => {
    setComponentPreference(TIMEFRAME, COLUMN_VISIBILITY_PREF_KEY, value);
  };

  const handleColumnResize = (params) => {
    const newWidth = params.width;
    const field = params.field || params.colDef?.field;

    if (field) {
      setColumnWidths(prev => {
        const updated = { ...prev, [field]: newWidth };
        setComponentPreference(TIMEFRAME, COLUMN_WIDTH_PREF_KEY, updated);
        return updated;
      });
    }
  };

  const handleSearchChange = (value) => {
    logger.log("SearchBar onChange:", value);
    sessionStorage.setItem(TIMEFRAME_SEARCH, value);
    setDebouncedSearchTerm(value);
  }

  return (
    <Box sx={DATAGRID_STYLE}>
      <FiltersWrapper>
        {/* Search Bar */}
        <SearchBar
          placeholder={"Search Time Frame"}
          value={debouncedSearchTerm}
          onChange={handleSearchChange}
        />

        <Box sx={FILTER_BUTTON_STYLE}>
          {/* Help Button */}
          <IconButton
            onClick={() => setHelpOpen(true)}
            aria-label="Help"
            color="primary"
            sx={{ height: 54, minWidth: 54 }}
          >
            <HelpIcon />
          </IconButton>

          {/* Add Button */}
          <AddButton title={"Time Frame"} onClick={handleAdd} isDisabled={isLoading} roleComponent={PAGE_KEYS.TIME_SETUP_FRAME} />

          {/* Download Button */}
          <ExportButton
            prefix={"Time Frame"}
            data={getGridData}
            roleComponent={PAGE_KEYS.TIME_SETUP_FRAME}
            isDisabled={isLoading}
          />
        </Box>
      </FiltersWrapper>

      <Paper elevation={2} sx={{ p: 2 }}>
        <Box sx={{ ...DG_CONFIG.HEIGHT_STYLES }}>
          <DataGrid
            aria-label="Time Frame data table"
            aria-labelledby="time-frame-table-title"
            rows={getGridData}
            columns={columns}
            initialState={{
              pagination: {
                paginationModel: { pageSize: initialPageSize },
              },
              sorting: {
                sortModel: initialSorting,
              },
              columns: {
                columnVisibilityModel: initialColumnVisibility,
              }
            }}
            pageSizeOptions={DG_CONFIG.PAGE_SIZE_OPTIONS}
            onPaginationModelChange={handlePageSizeChange}
            onSortModelChange={handleSortChange}
            onColumnResize={handleColumnResize}
            onColumnVisibilityModelChange={handleColumnVisibilityChange}
            disableRowSelectionOnClick
            loading={isLoading}
            slotProps={{
              loadingOverlay: DG_CONFIG.OVERLAY,
            }}
          />
        </Box>
      </Paper>

      {/* Dialog */}
      {dialogState != null && (
        <TimeFrameDialog
          onClose={() => {
            logger.log("Dialog closed");
            setDialogState(null);
          }}
          onSuccess={async () => {
            logger.log("Dialog success (add/edit)");
            await refetch();
          }}
          formData={formData}
          isEditing={dialogState === 2}
        />
      )}

      {helpOpen && (
        <TimeFrameHelp open={helpOpen} onClose={() => setHelpOpen(false)} />
      )}
    </Box>
  );
};

export default TimeFrame;
